import { cn } from '@seen/ui'
import { cva } from 'class-variance-authority'

/* The one height every top-of-pane row uses: both pane headers and the band the
 * canvas/list switcher is pinned into.
 *
 * Fixed rather than derived from padding, because the rows hold different
 * controls (a search input on the left, date/author/label pills on the right)
 * and padding-derived heights drift apart the moment either side changes.
 *
 * 60px, and the band renders at exactly 60 including its rule. Getting there
 * needs the rule out of the box: height is border-box, so `h-[60px]` plus a
 * `border-b` would leave a 59px content box, and the 40px controls centered in
 * an odd height land on a half pixel and render soft. This was 61px for exactly
 * that reason — 60px of content plus the border — which measured a pixel taller
 * than intended. */
export const DESK_PANE_HEADER_HEIGHT_CLASSNAME = 'h-[60px]'

/* Marks the canvas's "real-time sync disconnected" banner so the switcher band
 * can drop below it. Only present behind the flag: the collision it resolves is
 * with the switcher, which doesn't exist otherwise.
 *
 * An attribute rather than a prop or a store, because the banner is rendered by
 * whichever view is mounted — several levels below the band, which is the
 * *view's* sibling, not its child — and the room status it reads only exists
 * inside that view's Liveblocks room. Threading state up through two lazy
 * subtrees to carry one offset is far more machinery than the offset is worth. */
export const DESK_SYNC_BANNER_SELECTOR = '[data-desk-sync-banner]'

/* Marks the desk page wrapper as the scope the band's `:has()` looks in. Both
 * the banner and the band are inside it. */
export const DESK_VIEW_GROUP_CLASSNAME = 'group/desk-view'

/* The band drops a full header height while the banner is showing, so the two
 * stack as adjacent bands instead of overlapping.
 *
 * The banner keeps the top edge — it is the data-loss warning, and it stays
 * unshrunk, unhidden and above everything. What it may not do is swallow the
 * switcher: the banner covered it outright (`elementFromPoint` over the buttons
 * returned the banner), which left no way across to the other view at exactly
 * the moment something had gone wrong.
 *
 * The offset is the header height, and the banner is pinned to that same height
 * behind the flag, so they are one number rather than two that have to agree.
 * Written out in full because Tailwind only extracts literal class names. */
export const DESK_VIEW_TOGGLE_BANNER_DODGE_CLASSNAME =
  'group-has-[[data-desk-sync-banner]]/desk-view:top-[60px]'

/* The gutter between a pane's edge and its contents — every box in list view:
 * both pane headers and the note rows.
 *
 * The selected note is a filled, rounded card, so this cannot be folded into
 * the rows' own padding: with no gutter the card's rounded corners butt against
 * the pane edge and read as a clipping bug rather than as a card.
 *
 * One constant rather than one per box, because every edge that carries it has
 * to land on the same vertical: the list header's last control against the cards'
 * right edge, and the detail header's contents against the note body under them.
 * Which control is last is not fixed — the search field was, until the new-note
 * button was added after it — and that is the point of spending one constant
 * here instead of a per-box value that has to be retuned when the row changes.
 * They were three values — `pr-4` on the list header, whose left was spent on
 * `pl-24` of clearance for the floating switcher, and `px-6` on the detail
 * pane's two headers, which sat 8px out from their own editor body (`p-4` in
 * StickyNoteNode's editor props). Both reasons are gone: the switcher is a child
 * of the header now and reserves nothing, so there is one step and nothing left
 * for it to drift against. */
export const DESK_PANE_GUTTER_CLASSNAME = 'px-4'

/* Shared header row for both list-view panes, so their rules form one
 * continuous line across the divider.
 *
 * The rule is an `::after` pinned to the bottom edge rather than a border: it
 * overlays the last pixel instead of consuming one, which keeps the content box
 * an even 60px so `items-center` puts the 40px controls at exactly 10px. Same
 * reasoning as the note rows' divider. */
export const deskPaneHeaderVariants = cva(
  `relative flex shrink-0 items-center ${DESK_PANE_HEADER_HEIGHT_CLASSNAME} after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-border after:content-['']`,
  {
    variants: {
      placement: {
        row: DESK_PANE_GUTTER_CLASSNAME,
        /* The canvas's band for the switcher — canvas only. The canvas is a flow
         * pane with no header row of its own, so there the switcher has nothing
         * to be a child of and must float; the canvas already owns its other
         * three corners (add-note and options top-right, zoom controls
         * bottom-left, minimap bottom-right), which makes top-left the one free
         * slot.
         *
         * List view does *not* use this. There the switcher is an ordinary flex
         * child of the pane header, in the row beside the search field, which is
         * what lets that row's spacing be chosen rather than derived. Overlaid,
         * neither number was: the switcher's inset was the band's own `pl-2`,
         * and the space before the search field was whatever `pl-24` had left
         * over once the 74px group and that 8px had been subtracted — 14px that
         * nobody picked. As a child it takes an ordinary `gap`, and the header
         * stops spending its left edge on clearance for something that no longer
         * covers it.
         *
         * It is not a fix for vertical alignment: overlaid and in-row both
         * measure the two controls' boxes identical (top 10px, height 40px, in
         * the 60px band) at every container height tried, fractional ones
         * included. What changes is that one `items-center` now does the
         * centering instead of two boxes that have to be kept equal by hand —
         * which is what drifted when a border made the switcher 42px against a
         * 40px input, and again when the row grew 56px → 61px.
         *
         * It still reuses the header row's base so the switcher holds the same
         * position across both views: same 60px box and `items-center` for the
         * y, and `pl-4` matching the header's own gutter for the x, so switching
         * views doesn't move it.
         *
         * `after:hidden` suppresses the inherited rule — the canvas draws no
         * header line. `absolute` overrides the `relative` the base sets; both
         * reach the element through `cn`, so tailwind-merge resolves the
         * position conflict in favor of this one.
         *
         * `pointer-events-none` because the band is a full-height 60px strip
         * laid over the canvas: without it, a pan or marquee-select started in
         * the band's padding — above, below, or left of the buttons — hits the
         * overlay instead of the canvas. The switcher group re-enables pointer
         * events on just itself. */
        band: `absolute top-0 left-0 z-10 pl-4 after:hidden pointer-events-none ${DESK_VIEW_TOGGLE_BANNER_DODGE_CLASSNAME}`,
      },
    },
    defaultVariants: { placement: 'row' },
  }
)

/* The gap between the switcher and what shares its row: the search field in
 * list view, and the search button that opens it on the canvas band. One value
 * for both, because the two controls occupy the same slot in the same place —
 * the button is where the field is about to be, so a different gap would step
 * the row sideways on the way across. */
export const DESK_LIST_HEADER_GAP_CLASSNAME = 'gap-2'

/* Breathing room between the header's bottom rule and the first row. Without it
 * the first card sits flush under the rule and reads as an extension of the
 * header rather than as the first item in a list. 12px, matching the cards'
 * own padding, once 24px turned out to read as too loose in place. */
export const DESK_LIST_ROWS_TOP_INSET_CLASSNAME = 'pt-3'

export interface DeskNoteRowStyle {
  className: string
  /* Whether this row draws the rule that separates it from the row below. */
  showsDivider: boolean
}

/* One place decides both halves of the selected/unselected treatment, because
 * they are not independent: selection is carried by the fill *instead of* a
 * rule, so a selected row that kept its divider would draw a line across the
 * bottom of its own card.
 *
 * The geometry here is identical in both states — same padding, no conditional
 * spacing — and the rule is taken out of layout entirely (see the divider in
 * DeskNoteRow). An earlier version balanced the rule against the padding box
 * instead (`pt` + gap when unselected, `pb` when selected), which measured 109px
 * against 108px: uniform within a state, but selecting a row moved every row
 * below it down by a pixel. Only the fill may differ between the two states. */
export function deskNoteRowStyle(isSelected: boolean): DeskNoteRowStyle {
  return {
    className: cn(
      'relative flex flex-col p-3 w-full text-left rounded-md transition-colors',
      isSelected ? 'bg-muted' : 'hover:bg-muted/50'
    ),
    showsDivider: !isSelected,
  }
}
